import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell, Section, Toggle } from '../components/PageShell.tsx';
import { Select } from '../components/Select.tsx';
import { Avatar, Spinner } from '../components/ui.tsx';
import { ImageIcon, UserIcon } from '../components/icons.tsx';
import { SaveRow, useSaved } from '../components/workspace/shared.tsx';
import type { SettingsDto } from '../components/workspace/shared.tsx';
import { ApiRequestError, api, uploadAvatar } from '../lib/api.ts';
import { useTabRoute } from '../lib/useTabRoute.ts';
import { playMessageTone } from '../lib/useMessageSound.ts';
import { useAuth } from '../lib/auth.tsx';
import { useTheme } from '../lib/theme.ts';

/**
 * Settings is the signed-in person: their name, their composer, their sound,
 * their password.
 *
 * Everything that configures the workspace — the widget, automation, channels,
 * retention — moved to Management, which is where the rest of the workspace is
 * administered and which only managers can reach. Splitting them by whose
 * setting it is means an agent's Settings no longer shows three tabs they
 * cannot act on, and a manager stops hunting for the widget snippet in the same
 * screen as their own theme.
 */
const TABS = ['profile'] as const;
type Tab = (typeof TABS)[number];

export function SettingsPage(): React.JSX.Element {
  const [tab, setTab] = useTabRoute<Tab>('/settings', TABS, 'profile');

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsDto>('/settings'),
  });

  return (
    <PageShell
      title="Settings"
      description="Your name, how the composer behaves, and your password."
      tabs={[
        {
          id: 'profile',
          label: 'Profile',
          icon: UserIcon,
          help: {
            summary: 'Your name, how the composer behaves, and your password.',
            points: [
              'With "Send with Enter" off, \u2318/Ctrl+Enter sends and Enter inserts a line break.',
              'Changing your password signs out every other device, including this one elsewhere.',
              'Appearance is saved to your account, so a new browser picks it up.',
            ],
          },
        },
      ]}
      active={tab}
      onChange={setTab}
    >
      {settings.isLoading || !settings.data ? (
        <Spinner className="h-5 w-5 text-brand-600" />
      ) : (
        <ProfileTab settings={settings.data} />
      )}
    </PageShell>
  );
}


/** Matches MAX_UPLOAD_MB's default on the server; the point is the message. */
const MAX_AVATAR_BYTES = 8 * 1024 * 1024;

function ProfileTab({ settings }: { settings: SettingsDto }): React.JSX.Element {
  const queryClient = useQueryClient();
  const { refresh } = useAuth();
  const { theme, setTheme } = useTheme(settings.preferences.theme as 'light' | 'dark' | 'system');

  const [fullname, setFullname] = useState(settings.profile.displayName);
  const [title, setTitle] = useState(settings.chat.title);
  const [sendWithEnter, setSendWithEnter] = useState(settings.preferences.sendWithEnter);
  const [soundEnabled, setSoundEnabled] = useState(settings.preferences.soundEnabled);
  const [soundVolume, setSoundVolume] = useState(settings.preferences.soundVolume);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', verifyPassword: '' });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savedProfile, markProfileSaved] = useSaved();
  const [savedPassword, markPasswordSaved] = useSaved();

  const fileInput = useRef<HTMLInputElement>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  /** Both of these refresh the auth context as well as the settings query —
   *  the same picture is in the sidebar and on every message this person sent. */
  const afterAvatarChange = () => {
    setAvatarError(null);
    void refresh();
    void queryClient.invalidateQueries({ queryKey: ['settings'] });
  };

  const avatar = useMutation({
    mutationFn: (file: File) => {
      // Checked here as well as on the server, because a 12 MB photo from a
      // phone should fail in the moment rather than after the upload.
      if (file.size > MAX_AVATAR_BYTES) {
        throw new ApiRequestError('That image is larger than 8 MB', 0, 'bad_request');
      }

      return uploadAvatar(file);
    },
    onError: (error) => {
      setAvatarError(error instanceof ApiRequestError ? error.message : 'Could not upload that.');
    },
    onSuccess: afterAvatarChange,
  });

  const removeAvatar = useMutation({
    // An empty string is what the profile route reads as "clear it".
    mutationFn: () => api.put('/settings/profile', { fullname, title, avatar: '' }),
    onError: () => {
      setAvatarError('Could not remove it.');
    },
    onSuccess: afterAvatarChange,
  });

  const saveProfile = useMutation({
    mutationFn: async () => {
      await api.put('/settings/profile', { fullname, title });
      // `theme` has to travel with the rest: the select only wrote to
      // localStorage, so the stored preference never left this browser.
      await api.put('/settings/preferences', { sendWithEnter, soundEnabled, soundVolume, theme });
    },
    onSuccess: () => {
      markProfileSaved();
      void refresh();
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const changePassword = useMutation({
    mutationFn: () => api.post('/auth/change-password', passwords),
    onSuccess: () => {
      setPasswords({ currentPassword: '', newPassword: '', verifyPassword: '' });
      setPasswordError(null);
      markPasswordSaved();
    },
    onError: (caught: unknown) => {
      setPasswordError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong');
    },
  });

  return (
    <>
      <Section title="Profile">
        <div className="flex items-center gap-4">
          {/* The picture is the control. A separate "upload" button beside an
              image nobody thought was clickable is one element too many. */}
          <button
            type="button"
            className="group relative shrink-0 rounded-full"
            title="Change your picture"
            disabled={avatar.isPending}
            onClick={() => {
              fileInput.current?.click();
            }}
          >
            <Avatar src={settings.profile.avatar} name={fullname} size={56} />

            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-slate-900/60 text-white opacity-0 transition group-hover:opacity-100">
              {avatar.isPending ? <Spinner /> : <ImageIcon className="h-5 w-5" />}
            </span>
          </button>

          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared straight away, so picking the same file twice in a row
              // still fires a change event the second time.
              event.target.value = '';
              if (file) avatar.mutate(file);
            }}
          />

          <div className="min-w-0">
            <p className="text-sm text-slate-700 dark:text-slate-200">{settings.profile.email}</p>
            <p className="text-xs text-slate-500 capitalize dark:text-slate-400">
              {settings.profile.role}
            </p>

            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
                onClick={() => {
                  fileInput.current?.click();
                }}
              >
                {settings.profile.avatar ? 'Change picture' : 'Upload a picture'}
              </button>

              {settings.profile.avatar ? (
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:underline dark:text-slate-400"
                  onClick={() => {
                    removeAvatar.mutate();
                  }}
                >
                  Remove
                </button>
              ) : null}
            </div>

            {avatarError ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400" role="alert">
                {avatarError}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="lg-label" htmlFor="fullname">
              Display name
            </label>
            <input
              id="fullname"
              className="lg-input"
              value={fullname}
              onChange={(event) => {
                setFullname(event.target.value);
              }}
            />
          </div>

          {settings.profile.role === 'owner' ? (
            <div>
              <label className="lg-label" htmlFor="workspace">
                Workspace name
              </label>
              <input
                id="workspace"
                className="lg-input"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-5 space-y-3">
          <Toggle
            label="Send with Enter"
            description="Off means ⌘/Ctrl+Enter sends and Enter inserts a line break."
            checked={sendWithEnter}
            onChange={setSendWithEnter}
          />
          <Toggle
            label="Play a sound on new messages"
            checked={soundEnabled}
            onChange={setSoundEnabled}
          />

          {/* Only under the toggle that turns it on: a volume control for a
              sound that is off is a dead thing to reason about. */}
          {soundEnabled ? (
            <div className="flex flex-wrap items-center gap-3 pl-8">
              <label className="text-sm text-slate-600 dark:text-slate-300" htmlFor="sound-volume">
                Volume
              </label>
              <input
                id="sound-volume"
                type="range"
                min={10}
                max={100}
                step={5}
                className="lg-range w-44"
                value={soundVolume}
                onChange={(event) => {
                  setSoundVolume(Number(event.target.value));
                }}
                // Played on release rather than on every step: dragging across
                // the track would otherwise fire a burst of overlapping tones,
                // and you cannot judge a loudness you are hearing twenty of.
                onPointerUp={(event) => {
                  playMessageTone(Number(event.currentTarget.value));
                }}
                onKeyUp={(event) => {
                  playMessageTone(Number(event.currentTarget.value));
                }}
              />
              <span className="w-9 text-xs tabular-nums text-slate-400">{soundVolume}%</span>
              <button
                type="button"
                className="lg-btn-secondary text-xs"
                onClick={() => {
                  playMessageTone(soundVolume);
                }}
              >
                Play it
              </button>
            </div>
          ) : null}
        </div>

        <div className="mt-5">
          <label className="lg-label" htmlFor="theme">
            Appearance
          </label>
          <Select<'light' | 'dark' | 'system'>
            id="theme"
            className="sm:w-48"
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'system', label: 'Match system' },
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
        </div>

        <SaveRow
          pending={saveProfile.isPending}
          saved={savedProfile}
          onSave={() => {
            saveProfile.mutate();
          }}
        />
      </Section>

      <Section title="Change password">
        <div className="grid gap-4 sm:grid-cols-3">
          {(
            [
              ['currentPassword', 'Current password', 'current-password'],
              ['newPassword', 'New password', 'new-password'],
              ['verifyPassword', 'Confirm new password', 'new-password'],
            ] as const
          ).map(([key, label, autoComplete]) => (
            <div key={key}>
              <label className="lg-label" htmlFor={key}>
                {label}
              </label>
              <input
                id={key}
                type="password"
                autoComplete={autoComplete}
                className="lg-input"
                value={passwords[key]}
                onChange={(event) => {
                  setPasswords((previous) => ({ ...previous, [key]: event.target.value }));
                }}
              />
            </div>
          ))}
        </div>

        <SaveRow
          pending={changePassword.isPending}
          saved={savedPassword}
          error={passwordError}
          onSave={() => {
            changePassword.mutate();
          }}
        />
      </Section>
    </>
  );
}
